import { StateObject } from "@/dtos/State.dto";
import {
  EditTemplateFolder,
  GetTemplateFolder,
  RegisterTemplateFolder,
} from "@/dtos/TemplateFolder.dto";
import TemplateFolder from "@/services/template/template-folder";
import {
  CLEAR_TEMPLATE_FOLDER_ISLOADING,
  DELETE_TEMPLATE_FOLDER,
  EDIT_TEMPLATE_FOLDER,
  GET_TEMPLATE_FOLDERS,
  REGISTER_TEMPLATE_FOLDER,
} from "@/store/action-types";
import { SET_ERRORMSG, SET_TEMPLATE_FOLDER, SET_TEMPLATE_FOLDERS } from "@/store/mutation-types";
import { AxiosResponse } from "axios";
import { Module } from "vuex";

const templateFolder: Module<any, {}> = {
  state: {
    templateFolder: {
      data: null,
      isLoading: false,
    },
    templateFolders: {
      data: null,
      isLoading: false,
    },
  },
  getters: {
    templateFolder: (state: { templateFolder: any }) => state.templateFolder,
    templateFolders: (state: { templateFolders: any }) => state.templateFolders,
  },
  mutations: {
    [SET_TEMPLATE_FOLDER]: (
      state: { templateFolder: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.templateFolder.data = payload.data;
      state.templateFolder.isLoading = payload.isLoading;
    },
    [SET_TEMPLATE_FOLDERS]: (
      state: { templateFolders: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.templateFolders.data = payload.data;
      state.templateFolders.isLoading = payload.isLoading;
    },
  },
  actions: {
    [REGISTER_TEMPLATE_FOLDER]: async ({ commit }: any, payload: any) => {
      commit(SET_TEMPLATE_FOLDER, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await TemplateFolder.registerTemplateFolder(
          payload as RegisterTemplateFolder
        );

        if (result) {
          if (result.data.status) {
            commit(SET_TEMPLATE_FOLDER, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_TEMPLATE_FOLDER, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_TEMPLATE_FOLDER, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_TEMPLATE_FOLDER, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please try again.");
        } else if (error.response?.status === 500) {
          commit(SET_ERRORMSG, "Server error. Please try again.");
        } else if (error.response?.status === 404) {
          commit(SET_ERRORMSG, "Not found.");
        } else if (!error.response) {
          commit(SET_ERRORMSG, "Network error. Please check your connection.");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to save. Please try again."
          );
        }

        return false;
      }
    },
    [GET_TEMPLATE_FOLDERS]: async ({ commit }: any, payload: any) => {
      commit(SET_TEMPLATE_FOLDERS, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await TemplateFolder.getTemplateFolders(
          payload as GetTemplateFolder
        );

        if (result) {
          if (result.data.status) {
            commit(SET_TEMPLATE_FOLDERS, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_TEMPLATE_FOLDERS, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_TEMPLATE_FOLDERS, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_TEMPLATE_FOLDERS, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please try again.");
        } else if (error.response?.status === 500) {
          commit(SET_ERRORMSG, "Server error. Please try again.");
        } else if (error.response?.status === 404) {
          commit(SET_ERRORMSG, "Not found.");
        } else if (!error.response) {
          commit(SET_ERRORMSG, "Network error. Please check your connection.");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to load. Please try again."
          );
        }

        return false;
      }
    },
    [EDIT_TEMPLATE_FOLDER]: async ({ commit }: any, payload: any) => {
      commit(SET_TEMPLATE_FOLDER, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await TemplateFolder.editTemplateFolder(
          payload as EditTemplateFolder
        );

        if (result) {
          if (result.data.status) {
            commit(SET_TEMPLATE_FOLDER, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_TEMPLATE_FOLDER, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_TEMPLATE_FOLDER, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_TEMPLATE_FOLDER, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please try again.");
        } else if (error.response?.status === 500) {
          commit(SET_ERRORMSG, "Server error. Please try again.");
        } else if (error.response?.status === 404) {
          commit(SET_ERRORMSG, "Not found.");
        } else if (!error.response) {
          commit(SET_ERRORMSG, "Network error. Please check your connection.");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to save. Please try again."
          );
        }

        return false;
      }
    },
    [DELETE_TEMPLATE_FOLDER]: async ({ commit }: any, payload: string) => {
      commit(SET_TEMPLATE_FOLDER, { data: { id: payload }, isLoading: true });

      try {
        const result: AxiosResponse = await TemplateFolder.deleteTemplateFolder(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_TEMPLATE_FOLDER, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_TEMPLATE_FOLDER, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_TEMPLATE_FOLDER, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_TEMPLATE_FOLDER, { data: null, isLoading: false });

        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please try again.");
        } else if (error.response?.status === 500) {
          commit(SET_ERRORMSG, "Server error. Please try again.");
        } else if (error.response?.status === 404) {
          commit(SET_ERRORMSG, "Not found.");
        } else if (!error.response) {
          commit(SET_ERRORMSG, "Network error. Please check your connection.");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to delete. Please try again."
          );
        }

        return false;
      }
    },
    [CLEAR_TEMPLATE_FOLDER_ISLOADING]: ({ commit }) => {
      commit(SET_TEMPLATE_FOLDER, { isLoading: false });
      commit(SET_TEMPLATE_FOLDERS, { isLoading: false });
    },
  },
};

export default templateFolder;
